import {
  ComicCategoryEntity,
  ComicTypeEntity,
  LanguageEntity,
} from "../../../services/gc/types";
import { COMIC_TYPE_ALL } from "../components/filters/ComicTypeFilter";
import { COMIC_CATEGORY_ALL } from "../components/filters/ComicCategoryFilter";
import { LANGUAGE_ALL } from "../components/filters/LanguageFacetGroupFilter";
import { LICENSE_TYPE_ALL } from "../components/filters/LicenseTypeFilter";
import { ARTIST_TYPE_ALL } from "../components/filters/ArtistTypeFilter";
import {
  COMIC_FORMAT_ALL,
  COMIC_AUGMENTED_ENABLED,
} from "../components/filters/ComicFormatFilter";
import { COMIC_CONTRIBUTOR_TYPE_ALL } from "../components/filters/ComicContributerTypeFilter";
import { COMIC_USER_READING_STATUS_ALL } from "../../../components/readingStatusButton/constants";
import { CROWDFUNDING_TYPE_ALL } from "../components/filters/CrowdfundingFilter";
import { DEFAULT_SORT_ORDER_TYPE } from "../../../components/sortMenus/SortOrderMenu";
import { DEFAULT_SORT_DURATION_TYPE } from "../../../components/sortMenus/SortDurationMenu";
import { BrowseTabType } from "../../../types";

export type FilterMaps = {
  typesBySlug: Record<string, ComicTypeEntity>;
  typesById: Record<number, ComicTypeEntity>;
  genresBySlug: Record<string, ComicCategoryEntity>;
  genresById: Record<string | number, ComicCategoryEntity>;
  langsById: Record<string, LanguageEntity>;
};

export type FilterState = {
  comicEntityType: string;
  comicCategory: string;
  languageFacets: string;
  query: string;
  licenseType: string;
  artistEntityType: string;
  comicFormat: string;
  comicContributorType: string;
  comicUserReadingStatus: string;
  crowdfundingType: string;
  sortOrder: string;
  sortDuration: string;
  countryFacets: string[];
  comicFacets: Record<string, string[]>;
  listId: string;
  contributor: string;
};

const RELEVANCE_SORT_ORDER = "relevance";

function getImplicitDefaultSortOrder(query: string): string {
  return query ? RELEVANCE_SORT_ORDER : DEFAULT_SORT_ORDER_TYPE;
}

export const DEFAULT_FILTER_STATE: FilterState = {
  comicEntityType: COMIC_TYPE_ALL,
  comicCategory: COMIC_CATEGORY_ALL,
  languageFacets: LANGUAGE_ALL,
  query: "",
  licenseType: LICENSE_TYPE_ALL,
  artistEntityType: ARTIST_TYPE_ALL,
  comicFormat: COMIC_FORMAT_ALL,
  comicContributorType: COMIC_CONTRIBUTOR_TYPE_ALL,
  comicUserReadingStatus: COMIC_USER_READING_STATUS_ALL,
  crowdfundingType: CROWDFUNDING_TYPE_ALL,
  sortOrder: DEFAULT_SORT_ORDER_TYPE,
  sortDuration: DEFAULT_SORT_DURATION_TYPE,
  countryFacets: [],
  comicFacets: {},
  listId: "",
  contributor: "",
};

export function resolvePathSlugs(
  slug1: string | undefined,
  slug2: string | undefined,
  slug3: string | undefined,
  filterMaps: FilterMaps,
): { langId: string; typeId: string; genreId: string } {
  const { typesBySlug: types, genresBySlug: genres, langsById: langs } = filterMaps;

  let langId = LANGUAGE_ALL;
  let typeId = COMIC_TYPE_ALL;
  let genreId = COMIC_CATEGORY_ALL;

  if (slug1 && slug2 && slug3) {
    langId = langs[slug1]?.id ?? LANGUAGE_ALL;
    typeId = "" + (types[slug2]?.id ?? COMIC_TYPE_ALL);
    genreId = "" + (genres[slug3]?.id ?? COMIC_CATEGORY_ALL);
  } else if (slug1 && slug2) {
    if (langs[slug1]) {
      langId = langs[slug1].id;
      if (types[slug2]) {
        typeId = "" + types[slug2].id;
      } else {
        genreId = "" + (genres[slug2]?.id ?? COMIC_CATEGORY_ALL);
      }
    } else {
      typeId = "" + (types[slug1]?.id ?? COMIC_TYPE_ALL);
      genreId = "" + (genres[slug2]?.id ?? COMIC_CATEGORY_ALL);
    }
  } else if (slug1) {
    if (langs[slug1]) {
      langId = langs[slug1].id;
    } else if (genres[slug1]) {
      genreId = "" + genres[slug1].id;
    } else {
      typeId = "" + (types[slug1]?.id ?? COMIC_TYPE_ALL);
    }
  }

  return { langId, typeId, genreId };
}

export function parseFacets(searchParams: URLSearchParams): Record<string, string[]> {
  const facets: Record<string, string[]> = {};
  searchParams.forEach((value, key) => {
    const match = key.match(/^facets\[(\d+)\]\[\]$/);
    if (match) {
      const groupId = match[1];
      if (!facets[groupId]) {
        facets[groupId] = [];
      }
      facets[groupId].push(value);
    }
  });
  return facets;
}

export function parseFiltersFromUrl(
  slug1: string | undefined,
  slug2: string | undefined,
  slug3: string | undefined,
  searchParams: URLSearchParams,
  filterMaps: FilterMaps,
): FilterState {
  const { langId, typeId, genreId } = resolvePathSlugs(slug1, slug2, slug3, filterMaps);

  const premiumOnly = searchParams.get("premium_only");
  const licenseTypeId = searchParams.get("license_type_id");
  let licenseType = LICENSE_TYPE_ALL;
  if (premiumOnly === "true" || premiumOnly === "1") {
    licenseType = "collectible";
  } else if (licenseTypeId) {
    licenseType = licenseTypeId;
  }

  const query = searchParams.get("q") ?? "";
  const implicitDefaultSort = getImplicitDefaultSortOrder(query);
  let sortOrder = searchParams.get("sort") ?? implicitDefaultSort;
  const listId = searchParams.get("list") ?? searchParams.get("list_id") ?? "";

  // If sort is "list" but no listId, fall back to default
  if (sortOrder === "list" && !listId) {
    sortOrder = implicitDefaultSort;
  }

  return {
    comicEntityType: typeId,
    comicCategory: genreId,
    languageFacets: langId,
    query,
    licenseType,
    artistEntityType: searchParams.get("artist_type_id") ?? ARTIST_TYPE_ALL,
    comicFormat:
      searchParams.get("is_augmented") === "1"
        ? COMIC_AUGMENTED_ENABLED
        : (searchParams.get("comic_format_id") ?? COMIC_FORMAT_ALL),
    comicContributorType:
      searchParams.get("comic_contributor_type_id") ?? COMIC_CONTRIBUTOR_TYPE_ALL,
    comicUserReadingStatus:
      searchParams.get("comic_user_reading_status_id") ?? COMIC_USER_READING_STATUS_ALL,
    crowdfundingType: searchParams.get("crowdfunding") ?? CROWDFUNDING_TYPE_ALL,
    sortOrder,
    sortDuration: searchParams.get("sd") ?? DEFAULT_SORT_DURATION_TYPE,
    countryFacets: searchParams.getAll("country_id[]"),
    comicFacets: parseFacets(searchParams),
    listId,
    contributor: searchParams.get("contributor") ?? "",
  };
}

export function buildUrlPath(
  filters: FilterState,
  filterMaps: FilterMaps,
  tab: BrowseTabType,
): string {
  const { typesById, genresById, langsById } = filterMaps;

  const typeId = parseInt(filters.comicEntityType);
  const genreId = parseInt(filters.comicCategory);
  const langId = filters.languageFacets;

  const type = typesById[typeId];
  const genre = genresById[genreId];
  const lang = langsById[langId];

  let basePath: string;
  switch (tab) {
    case BrowseTabType.releases:
      basePath = "/books";
      break;
    case BrowseTabType.creators:
      basePath = "/creators";
      break;
    case BrowseTabType.bundles:
      basePath = "/bundles";
      break;
    default:
      basePath = "/browse";
      break;
  }

  let path = basePath;
  if (lang) path += `/${lang.id}`;
  if (type) path += `/${type.slug}`;
  if (genre) path += `/${genre.slug}`;

  return path;
}

export function buildApiParams(filters: FilterState, tab: BrowseTabType): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.comicEntityType && filters.comicEntityType !== COMIC_TYPE_ALL) {
    params.set("comic_type_id", filters.comicEntityType);
  }

  if (filters.comicCategory && filters.comicCategory !== COMIC_CATEGORY_ALL) {
    params.set("comic_genre_id", filters.comicCategory);
  }

  if (filters.licenseType && filters.licenseType !== LICENSE_TYPE_ALL) {
    if (filters.licenseType === "collectible") {
      if (tab === BrowseTabType.releases) {
        params.set("premium_only", "1");
      }
    } else if (filters.licenseType === "download_pdf") {
      params.set("license_type_id", filters.licenseType);
    } else {
      params.set("license_type_id", filters.licenseType);
      if (filters.licenseType === "3") {
        if (tab === BrowseTabType.releases) {
          params.set("premium_only", "0");
        }
      }
    }
  }

  if (filters.artistEntityType && filters.artistEntityType !== ARTIST_TYPE_ALL) {
    params.set("artist_type_id", filters.artistEntityType);
  }

  if (filters.comicFormat) {
    if (filters.comicFormat === COMIC_AUGMENTED_ENABLED) {
      params.set("is_augmented", "1");
    } else if (filters.comicFormat !== COMIC_FORMAT_ALL) {
      params.set("comic_format_id", filters.comicFormat);
    }
  }

  if (
    filters.comicContributorType &&
    filters.comicContributorType !== COMIC_CONTRIBUTOR_TYPE_ALL
  ) {
    params.set("comic_contributor_type_id", filters.comicContributorType);
  }

  if (
    filters.comicUserReadingStatus &&
    filters.comicUserReadingStatus !== COMIC_USER_READING_STATUS_ALL
  ) {
    params.set("comic_user_reading_status_id", filters.comicUserReadingStatus);
  }

  if (filters.crowdfundingType && filters.crowdfundingType !== CROWDFUNDING_TYPE_ALL) {
    params.set("crowdfunding", filters.crowdfundingType);
  }

  if (
    filters.sortOrder &&
    filters.sortOrder !== getImplicitDefaultSortOrder(filters.query)
  ) {
    params.set("sort", filters.sortOrder);
  }

  if (filters.sortDuration && filters.sortDuration !== DEFAULT_SORT_DURATION_TYPE) {
    params.set("sd", filters.sortDuration);
  }

  Object.entries(filters.comicFacets).forEach(([groupId, selectedFacets]) => {
    if (selectedFacets?.length) {
      selectedFacets.forEach((facet) => {
        params.append(`facets[${groupId}][]`, facet);
      });
    }
  });

  if (filters.countryFacets?.length) {
    filters.countryFacets.forEach((facet) => {
      params.append("country_id[]", facet);
    });
  }

  if (filters.languageFacets && filters.languageFacets !== LANGUAGE_ALL) {
    params.set("lang_id[]", filters.languageFacets);
  }

  if (filters.query) {
    params.set("q", filters.query);
  }

  if (filters.listId) {
    params.set("sort", "list");
    params.set("list_id", filters.listId);
  }

  if (filters.contributor) {
    params.set("contributor", filters.contributor);
  }

  return params;
}

export function buildSearchParams(filters: FilterState, _filterMaps: FilterMaps, tab: BrowseTabType): URLSearchParams {
  // Build full API params, then remove the ones encoded in the path
  const allParams = buildApiParams(filters, tab);

  // These are encoded in the URL path, not query string
  allParams.delete("comic_type_id");
  allParams.delete("comic_genre_id");
  allParams.delete("lang_id[]");

  return allParams;
}

/**
 * Get slug filter entities for title generation and other display purposes.
 */
export function getSlugFilterEntities(
  slug1: string | undefined,
  slug2: string | undefined,
  slug3: string | undefined,
  filterMaps: FilterMaps | null,
) {
  if (!filterMaps) return {};
  const { typesBySlug: types, genresBySlug: genres, langsById: langs } = filterMaps;

  let result: {
    genre?: ComicCategoryEntity;
    type?: ComicTypeEntity;
    lang?: LanguageEntity;
  } = {};

  if (slug1 && slug2 && slug3) {
    result = {
      lang: langs[slug1],
      type: types[slug2],
      genre: genres[slug3],
    };
  } else if (slug1 && slug2) {
    if (langs[slug1]) {
      if (types[slug2]) {
        result = { lang: langs[slug1], type: types[slug2] };
      } else {
        result = { lang: langs[slug1], genre: genres[slug2] };
      }
    } else {
      result = { type: types[slug1], genre: genres[slug2] };
    }
  } else if (slug1) {
    if (langs[slug1]) {
      result = { lang: langs[slug1] };
    } else if (genres[slug1]) {
      result = { genre: genres[slug1] };
    } else {
      result = { type: types[slug1] };
    }
  }

  return result;
}
